import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Asset } from '../types';
import { Search, Filter, Plus, Monitor, Server, Wifi, Shield, Printer } from 'lucide-react';
const mockAssets: Asset[] = [{
  id: '1',
  name: 'NB-001-MÜLLER',
  type: 'Notebook',
  client: 'Müller GmbH',
  serialNumber: 'SN839292',
  status: 'online',
  lastSeen: 'vor 5 Min',
  ipAddress: '192.168.1.105'
}, {
  id: '2',
  name: 'SRV-DC-01',
  type: 'Server',
  client: 'Müller GmbH',
  serialNumber: 'SRV99283',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '192.168.1.10'
}, {
  id: '3',
  name: 'FW-HQ-01',
  type: 'Firewall',
  client: 'Schmidt KG',
  serialNumber: 'WG-T40-992',
  status: 'warning',
  lastSeen: 'vor 2 Std',
  ipAddress: '10.0.0.1'
}, {
  id: '4',
  name: 'SW-CORE-01',
  type: 'Switch',
  client: 'Schmidt KG',
  serialNumber: 'UBI-SW-24',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '10.0.0.2'
}, {
  id: '5',
  name: 'PC-RECEPTION',
  type: 'PC',
  client: 'Praxis Dr. Stein',
  serialNumber: 'DELL-3929',
  status: 'offline',
  lastSeen: 'vor 2 Tagen',
  ipAddress: '192.168.178.20'
}, {
  id: '6',
  name: 'AP-LOBBY',
  type: 'Access Point',
  client: 'Praxis Dr. Stein',
  serialNumber: 'UBI-AP-PRO',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '192.168.178.5'
}];
interface AssetManagementProps {
  onLogout: () => void;
}
export function AssetManagement({
  onLogout
}: AssetManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const getIcon = (type: Asset['type']) => {
    switch (type) {
      case 'Notebook':
      case 'PC':
        return <Monitor className="h-4 w-4 text-slate-500" data-id="element-256" />;
      case 'Server':
        return <Server className="h-4 w-4 text-purple-500" data-id="element-257" />;
      case 'Firewall':
        return <Shield className="h-4 w-4 text-red-500" data-id="element-258" />;
      case 'Switch':
      case 'Access Point':
        return <Wifi className="h-4 w-4 text-blue-500" data-id="element-259" />;
      case 'Printer':
        return <Printer className="h-4 w-4 text-amber-500" data-id="element-260" />;
      default:
        return <Monitor className="h-4 w-4 text-slate-500" data-id="element-261" />;
    }
  };
  const getStatusBadge = (status: Asset['status']) => {
    switch (status) {
      case 'online':
        return <Badge variant="success" data-id="element-262">Online</Badge>;
      case 'offline':
        return <Badge variant="error" data-id="element-263">Offline</Badge>;
      case 'warning':
        return <Badge variant="warning" data-id="element-264">Warnung</Badge>;
      case 'maintenance':
        return <Badge variant="info" data-id="element-265">Wartung</Badge>;
      default:
        return <Badge variant="secondary" data-id="element-266">{status}</Badge>;
    }
  };
  const filteredAssets = mockAssets.filter(asset => (clientFilter === 'all' || asset.client === clientFilter) && (asset.name.toLowerCase().includes(searchTerm.toLowerCase()) || asset.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) || asset.ipAddress?.includes(searchTerm)));
  return <MainLayout title="IT Asset Management" onLogout={onLogout} data-id="element-267">
      <Card data-id="element-268">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-start md:items-center" data-id="element-269">
          <div className="flex gap-4 flex-1 w-full md:w-auto" data-id="element-270">
            <div className="w-full md:w-64" data-id="element-271">
              <Input placeholder="Suchen (Name, IP, Serial)..." icon={<Search className="h-4 w-4" data-id="element-273" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-272" />
            </div>
            <div className="w-full md:w-48" data-id="element-274">
              <Select options={[{
              value: 'all',
              label: 'Alle Kunden'
            }, {
              value: 'Müller GmbH',
              label: 'Müller GmbH'
            }, {
              value: 'Schmidt KG',
              label: 'Schmidt KG'
            }, {
              value: 'Praxis Dr. Stein',
              label: 'Praxis Dr. Stein'
            }]} value={clientFilter} onChange={e => setClientFilter(e.target.value)} data-id="element-275" />
            </div>
            <Button variant="outline" icon={<Filter className="h-4 w-4" data-id="element-277" />} data-id="element-276">
              Filter
            </Button>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-279" />} data-id="element-278">Asset anlegen</Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-280">
          <table className="w-full text-sm text-left" data-id="element-281">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-282">
              <tr data-id="element-283">
                <th className="px-4 py-3 font-medium" data-id="element-284">Name / Typ</th>
                <th className="px-4 py-3 font-medium" data-id="element-285">Kunde</th>
                <th className="px-4 py-3 font-medium" data-id="element-286">IP / Serial</th>
                <th className="px-4 py-3 font-medium" data-id="element-287">Status</th>
                <th className="px-4 py-3 font-medium" data-id="element-288">Letzter Kontakt</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-289">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-290">
              {filteredAssets.map(asset => <tr key={asset.id} className="hover:bg-slate-50 transition-colors" data-id="element-291">
                  <td className="px-4 py-3" data-id="element-292">
                    <div className="flex items-center gap-3" data-id="element-293">
                      <div className="p-2 bg-slate-100 rounded-lg" data-id="element-294">
                        {getIcon(asset.type)}
                      </div>
                      <div data-id="element-295">
                        <div className="font-medium text-slate-900" data-id="element-296">
                          {asset.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-297">
                          {asset.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-298">{asset.client}</td>
                  <td className="px-4 py-3" data-id="element-299">
                    <div className="text-slate-900" data-id="element-300">
                      {asset.ipAddress || '-'}
                    </div>
                    <div className="text-xs text-slate-500 font-mono" data-id="element-301">
                      {asset.serialNumber}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-302">{getStatusBadge(asset.status)}</td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-303">{asset.lastSeen}</td>
                  <td className="px-4 py-3 text-right" data-id="element-304">
                    <Button variant="ghost" size="sm" data-id="element-305">
                      Details
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {filteredAssets.length === 0 && <div className="text-center py-12 text-slate-500" data-id="element-306">
            Keine Assets gefunden.
          </div>}
      </Card>
    </MainLayout>;
}